import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import Message from '../../components/ui/Message';
import { Tariff } from '../../models/interfaces';
import { TariffsContext } from '../../context/tariff/TariffContext';


const formatPercent = (value: number) => {
    const num = Number(value);
    return isNaN(num) ? '-' : `${num.toLocaleString('en-US', { maximumFractionDigits: 4 })} %`;
};

export const TariffsPage = () => {
    const { state } = useLocation();
    const merchant = state?.merchant;

    const [buscar, setBuscar] = useState('');
    const [activeCurrency, setActiveCurrency] = useState<'all' | 188 | 840>('all');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [tariffToDelete, setTariffToDelete] = useState<Tariff | null>(null);

    const { tariffs, channels, banks, brands, paymentTypes, loadCatalogs, loadTariffs, deleteTariff } = useContext(TariffsContext);
    const navigate = useNavigate();

    useEffect(() => {
        if (!merchant) return;
        const load = async () => {
            setIsLoading(true);
            try {
                await loadCatalogs();
                await loadTariffs(merchant.id, 0);
            } catch (e) {
                setError('No se pudieron cargar las tarifas del comercio, intente de nuevo.');
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, [])

    const getPaymentTypeName = (paymentTypeId: string) =>
        paymentTypes.find((paymentType) => paymentType.code == paymentTypeId)?.description ?? '-';

    const getBrandName = (brandId: number) =>
        brands.find((brand) => brand.code == brandId)?.description ?? '-';

    const getBankName = (bankId: number) =>
        banks.find((bank) => bank.code == bankId)?.description ?? '-';

    const getChannelName = (channelId: number) =>
        channels.find((channel) => channel.code == channelId)?.description ?? '-';

    const counts = useMemo(() => ({
        all: tariffs.length,
        188: tariffs.filter(t => t.currencyId == 188).length,
        840: tariffs.filter(t => t.currencyId == 840).length,
    }), [tariffs]);

    const filteredData = useMemo(() => {
        const term = buscar.trim().toLowerCase();
        return tariffs
            .filter(t => activeCurrency === 'all' || t.currencyId == activeCurrency)
            .filter(t => !term ||
                [t.description, getPaymentTypeName(t.paymentTypeId), getBrandName(t.brandId), getBankName(t.bankId), getChannelName(t.channelId)]
                    .some(val => String(val ?? '').toLowerCase().includes(term)));
    }, [tariffs, buscar, activeCurrency, paymentTypes, brands, banks, channels]);

    // Sin comercio (ej. acceso directo a la URL) se vuelve al listado
    if (!merchant) return <Navigate to="/merchants" replace />;

    const handleNew = () => {
        navigate("/merchant/tariff", { state: { merchant } });
    }

    const handleEdit = (tariff: Tariff) => {
        navigate("/merchant/tariff", { state: { merchant, tariff } });
    }

    const processDelete = async () => {
        const tariff = tariffToDelete;
        setTariffToDelete(null);
        if (!tariff) return;
        const resp = await deleteTariff(tariff);
        if (resp !== "success") {
            setError(resp);
        }
    }

    const getKeyId = (tariff: Tariff) =>
        [tariff.paymentTypeId, tariff.brandId, tariff.bankId, tariff.currencyId, tariff.channelId].join('-');

    const tabs: { key: 'all' | 188 | 840; label: string }[] = [
        { key: 'all', label: 'Todas' },
        { key: 188, label: 'Colones' },
        { key: 840, label: 'Dólares' },
    ];

    return (
        <div className='mt-5 container-fluid px-4' style={styles.container}>

            <div className="d-flex flex-wrap justify-content-between align-items-start mb-4">
                <div>
                    <h3 className="mb-1">Tarifas</h3>
                    <p className="text-muted mb-0">Comisiones configuradas para el comercio <span className="fw-semibold text-body">{merchant.name}</span>.</p>
                </div>
                <div className="d-flex gap-2 mt-2 mt-md-0">
                    <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/merchants')}>
                        <i className="bi bi-arrow-left me-1" /> Volver a comercios
                    </button>
                    <button type="button" className="btn btn-primary" onClick={handleNew}>
                        <i className="bi bi-plus-lg me-1" /> Agregar tarifa
                    </button>
                </div>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-body">

                    <ul className="nav nav-tabs mb-3">
                        {tabs.map(tab => (
                            <li className="nav-item" key={tab.key}>
                                <button
                                    className={`nav-link ${activeCurrency === tab.key ? 'active' : ''}`}
                                    onClick={() => setActiveCurrency(tab.key)}
                                >
                                    {tab.label} ({counts[tab.key]})
                                </button>
                            </li>
                        ))}
                    </ul>

                    <div className="row g-2 mb-3">
                        <div className="col-md-6">
                            <div className="input-group">
                                <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                                <input
                                    className="form-control"
                                    id="search"
                                    type="text"
                                    placeholder="Buscar por descripción, tipo de pago, marca, banco o canal..."
                                    value={buscar}
                                    onChange={(e) => setBuscar(e.target.value)}
                                />
                                {buscar && (
                                    <button className="btn btn-outline-secondary" type="button" title="Limpiar búsqueda" onClick={() => setBuscar('')}>
                                        <i className="bi bi-x-lg" />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="table-responsive">
                        <table id='tariffTable' className="table table-hover align-middle">
                            <thead>
                                <tr className="text-muted small text-uppercase">
                                    <th>Descripción</th>
                                    <th>Tipo de pago</th>
                                    <th>Marca</th>
                                    <th>Banco</th>
                                    <th>Moneda</th>
                                    <th>Canal</th>
                                    <th className="text-end">Comisión</th>
                                    <th className="text-end" style={{ whiteSpace: 'nowrap' }}>Adel. renta</th>
                                    <th className="text-end">Servicio</th>
                                    <th style={{ textAlign: 'center' }}>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan={10} className='text-center py-4'>
                                        <span className="spinner-border spinner-border-sm me-2" /> Cargando...
                                    </td></tr>
                                ) : filteredData.length > 0 ? (
                                    filteredData.map(tariff => (
                                        <tr key={getKeyId(tariff)}>
                                            <td className="fw-medium">{tariff.description}</td>
                                            <td>{getPaymentTypeName(tariff.paymentTypeId)}</td>
                                            <td>{getBrandName(tariff.brandId)}</td>
                                            <td>{getBankName(tariff.bankId)}</td>
                                            <td>
                                                <span className={`badge rounded-pill ${tariff.currencyId == 188 ? 'bg-primary-subtle text-primary-emphasis' : 'bg-success-subtle text-success-emphasis'}`}>
                                                    {tariff.currencyId == 188 ? 'CRC' : 'USD'}
                                                </span>
                                            </td>
                                            <td>{getChannelName(tariff.channelId)}</td>
                                            <td className="text-end fw-semibold" style={{ whiteSpace: 'nowrap' }}>{formatPercent(tariff.commission)}</td>
                                            <td className="text-end" style={{ whiteSpace: 'nowrap' }}>{formatPercent(tariff.advanceRent)}</td>
                                            <td className="text-end" style={{ whiteSpace: 'nowrap' }}>{formatPercent(tariff.service)}</td>
                                            <td style={{ textAlign: 'center', width: 110 }}>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-primary"
                                                    title="Editar tarifa"
                                                    onClick={() => handleEdit(tariff)}
                                                >
                                                    <i className="bi bi-pencil" />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-danger ms-1"
                                                    title="Eliminar tarifa"
                                                    onClick={() => setTariffToDelete(tariff)}
                                                >
                                                    <i className="bi bi-trash" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan={10} className='text-center py-4 text-muted'>
                                        {buscar || activeCurrency !== 'all' ? 'No se encontraron tarifas para el filtro seleccionado.' : 'No hay tarifas configuradas.'}
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div style={{ height: '60px' }}></div>

            {error.length > 0 && (
                <Message type="error" message={error} closeDialog={() => setError('')} handleOk={() => setError('')} backgroundColor="rgba(0,0,0,0.4)" />
            )}

            {tariffToDelete && (
                <Message
                    type="success"
                    title="Atención"
                    message={`¿Está seguro que desea eliminar la tarifa "${tariffToDelete.description}"?`}
                    closeDialog={() => setTariffToDelete(null)}
                    showCancel={true}
                    handleCancel={() => setTariffToDelete(null)}
                    handleOk={processDelete}
                    backgroundColor="rgba(0,0,0,0.4)"
                />
            )}
        </div>
    )
}


const styles = {
    container: {
        flex: 1,
        marginTop: 10,
    }
};
